import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import {
  getActiveContractId,
  readStoredContractId,
  setActiveContractId,
} from "../api/activeContract";
import { getUserToken } from "../function/AuthStorage";

/**
 * سياق العقد في الواجهة.
 *
 * مصدره `GET /api/ContractContext` — والخادم هو من يقرّر ما يراه المستخدم
 * من عقود وأيّها نشط. والواجهة لا تُصفّي شيئاً بنفسها: ما لا يصل إليها لا
 * تعرضه، وما يصلها مصرّح به أصلاً.
 */

const ContractContext = createContext({
  loading: true,
  contracts: [],
  activeContractId: null,
  activeContract: null,
  showSelector: false,
  unrestricted: false,
  setActiveContract: () => {},
  reload: () => {},
});

export function useContracts() {
  return useContext(ContractContext);
}

export function ContractProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    contracts: [],
    activeContractId: getActiveContractId(),
    showSelector: false,
    unrestricted: false,
  });

  const load = useCallback(async () => {
    // بلا رمز لا سياق: النداء سيرتدّ 401 ويخرج المستخدم من النظام.
    if (!getUserToken()) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    try {
      const { data } = await apiClient.get("ContractContext");

      const contracts = Array.isArray(data?.contracts) ? data.contracts : [];
      const allowed = new Set(contracts.map((c) => c.id));

      // المحفوظ قد يكون لعقد حساب آخر استُعمل على هذا الجهاز، أو لعقد سُحب
      // من المستخدم. فلا يُقبل إلا إن كان ضمن ما أعاده الخادم.
      const stored = readStoredContractId();
      const resolved =
        stored && allowed.has(stored) ? stored : data?.activeContractId ?? null;

      setActiveContractId(resolved);

      setState({
        loading: false,
        contracts,
        activeContractId: resolved,
        showSelector: Boolean(data?.showSelector),
        unrestricted: Boolean(data?.unrestricted),
      });
    } catch {
      // تعذّر السياق لا يُعطّل التطبيق: بلا عقد نشط يعمل النظام كما كان
      // قبل تعدّد العقود — يرى المستخدم ما تسمح به صلاحياته.
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setActiveContract = useCallback((id) => {
    setActiveContractId(id);

    /*
      إعادة التحميل بعد التبديل — لا تحديث الحالة وحدها.

      العقد يغيّر **كل** ما على الشاشة: القوائم والسلال والأرقام والتقارير،
      وأكثرها محمَّل في مكوّنات لا تعرف بالتبديل. وتركها بمعروض عقدٍ وترويسة
      عقدٍ آخر أسوأ من إعادة تحميل صريحة يراها المستخدم.
    */
    window.location.reload();
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      activeContract:
        state.contracts.find((c) => c.id === state.activeContractId) ?? null,
      setActiveContract,
      reload: load,
    }),
    [state, setActiveContract, load]
  );

  return (
    <ContractContext.Provider value={value}>{children}</ContractContext.Provider>
  );
}

export default ContractContext;
